import React, { FC, useState } from 'react';

import { PrimaryButton, Dropdown, CuiOptionType } from '@crehana/ui';

import { Crehanaicons167 } from '@crehana/icons';

import { useTranslation } from '@crehana/i18n';

import { createUrlWithSourceQueryParams } from '@crehana/analytics';

import PaymentsLoader from 'Jsx/CheckoutV4/shared/PaymentsLoader';

import { PaymentSourceEnum } from 'Jsx/Types/Graphql/globals.payments';

import useSavedCards from 'Jsx/Shared/Hooks/useSavedCards';

import { CustomerCard } from 'Jsx/CheckoutV4/types';

import usePayment from '../hooks/usePayment';

import CardLabel from './CardLabel';

type PayProps = {
  itemId: number;
  itemType: string;
  urlToBuyNow: string;
  source: string;
  sourcePage: string;
  paymentSource: PaymentSourceEnum;
};

export type SelectedCard = CuiOptionType & Partial<CustomerCard>;

const PaymentWithSavedCard: FC<PayProps> = ({
  itemId,
  source,
  itemType,
  paymentSource,
  urlToBuyNow,
  sourcePage,
}) => {
  const { t } = useTranslation();
  const { savedCards } = useSavedCards();

  const [paymentError, setPaymentError] = useState('');
  const { paymentMutation, loading } = usePayment();
  const initialSavedCard = savedCards.find(card => card?.defaultCard);
  const [selectedCard, setSelectedCard] = useState<SelectedCard | undefined>({
    ...initialSavedCard,
    value: initialSavedCard?.cardNumber?.toString() || '',
    label: initialSavedCard?.cardNumber?.toString() || '',
    jsxLabel: CardLabel(initialSavedCard?.cardNumber),
  });

  const urlToCheckout = createUrlWithSourceQueryParams({
    url: urlToBuyNow,
    source: sourcePage,
    sourceDetail: 'Saved Card',
  });

  const onClickPay = () => {
    if (selectedCard?.cardUsa) {
      paymentMutation({
        variables: {
          paymentGateway: selectedCard?.paymentGateway?.toString() || '',
          itemId: itemId.toString() || '',
          itemType,
          stripeCardId: selectedCard?.cardUsa || '',
          stripeMexicoCardId: selectedCard?.cardMx || '',
          source,
          paymentSource,
        },
      })
        .then(({ data }) => {
          if (data?.payment?.success) {
            window.location.href = data.payment.redirectUrl || '';
          } else {
            setPaymentError(t('DEFAULT_PAYMENT_ERROR'));
          }
        })
        .catch(err => {
          setPaymentError(t('DEFAULT_PAYMENT_ERROR'));
          // @ts-expect-error
          window.Sentry.withScope(scope => {
            scope.setTag('paymentMethod', 'savedCard');
            scope.setExtra(source);
            window.Sentry.captureException(err);
          });
        });
    }
  };

  return (
    <div data-testid="course_one_purchase">
      <Dropdown
        full
        selected={selectedCard}
        options={
          savedCards?.map((card, index) => ({
            value: index.toString(),
            label: card?.cardNumber?.toString() || '',
            jsxLabel: CardLabel(card?.cardNumber),
            ...card,
          })) || []
        }
        onSelected={setSelectedCard}
        className="mb-16 rounded-10"
      />
      <PrimaryButton
        isAccent
        full
        onClick={onClickPay}
        label="Compra directa"
        data-testid="course_buy_one_purchase"
        disabled={!selectedCard?.value}
        size="xl"
        className="rounded-10"
      />
      <a
        className="flex items-center w-full mt-16 font-link text-secondary-main"
        href={urlToCheckout}
      >
        <span className="mr-4">{t('GO_PAYMENT_METHODS')}</span>
        <Crehanaicons167
          size={16}
          ignoreColor
          className="fill-current text-secondary-main"
        />
      </a>

      {paymentError && (
        <p className="font-body4 text-red-main mt-12">{paymentError}</p>
      )}
      {loading && (
        <PaymentsLoader
          type="purchaseOnline"
          cardType={selectedCard?.cardBrand || ''}
        />
      )}
    </div>
  );
};

export default PaymentWithSavedCard;
